import { Cultures } from '@/types/enums'
import { PHASE_PRODUCTION_BUILD } from 'next/dist/shared/lib/constants'

export interface ClientEnvironment {
  umbracoBaseUrl: string
  recaptchaSiteKey: string
  cookieBotDomainGroupId: string
  cmsHomePageCulture: string
  cmsHomePageRoute?: string
  cmsStartItem?: string
}

export class TestClientEnvironment implements ClientEnvironment {
  constructor() {
    this.umbracoBaseUrl = 'test'
    this.umbracoFormsDeliveryApiKey = 'test'
    this.recaptchaSiteKey = 'test'
    this.cookieBotDomainGroupId = 'test'
    this.cmsHomePageCulture = Cultures['en-US']
    this.cmsHomePageRoute = 'investors'
    this.cmsStartItem = 'bouwinvest-corporate-website-collection'
  }
  umbracoBaseUrl: string
  umbracoFormsDeliveryApiKey: string
  recaptchaSiteKey: string
  cookieBotDomainGroupId: string
  cmsHomePageCulture: string
  cmsHomePageRoute: string
  cmsStartItem?: string
}

export interface GlobalConfig {
  clientEnvironment: ClientEnvironment
  umbracoDeliveryPreviewApiKey: string
  gtmId: string
  cmsHomePageRoute: string
  draftToken: string
  revalidateToken: string
  applicationBaseUrl: string
  umbracoFormsDeliveryApiKey: string
  cacheOverviewDataTTLInSeconds: number
  algoliaAppId: string
  algoliaSearchApiKey: string
  algoliaIndexName: string
}

let environmentConfig: GlobalConfig

export const getGlobalEnvironmentConfig = (): GlobalConfig => {
  if (environmentConfig) {
    return environmentConfig
  }

  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
    throw new Error('Tried to read global config during production build')
  }

  let cmsHomePageRoute = process.env.CMS_HOMEPAGE_ROUTE
  if (!cmsHomePageRoute) {
    throw new Error(`Homepage route not configured, value: ${cmsHomePageRoute}`)
  }

  let cmsStartItem = process.env.CMS_START_ITEM
  if (!cmsStartItem) {
    throw new Error(`CMS start item not configured, value: ${cmsStartItem}`)
  }

  let algoliaAppId = process.env.ALGOLIA_APP_ID
  if (!algoliaAppId) {
    throw new Error(
      `Algolia app id item not configured, value: ${algoliaAppId}`,
    )
  }

  let algoliaSearchApiKey = process.env.ALGOLIA_SEARCH_API_KEY
  if (!algoliaSearchApiKey) {
    throw new Error(
      `Algolia search api key item not configured, value: ${algoliaSearchApiKey}`,
    )
  }

  let algoliaIndexName = process.env.ALGOLIA_INDEX_NAME
  if (!algoliaIndexName) {
    throw new Error(
      `Algolia index name not configured, value: ${algoliaIndexName}`,
    )
  }

  let cmsHomePageCulture = process.env.CMS_HOMEPAGE_CULTURE
  if (!cmsHomePageCulture) {
    throw new Error(
      `Homepage culture not configured, value: ${cmsHomePageCulture}`,
    )
  }

  let umbracoBaseUrl = process.env.UMBRACO_BASE_URL
  if (!umbracoBaseUrl) {
    throw new Error(`Umbraco URL not configured, value: ${umbracoBaseUrl}`)
  }
  umbracoBaseUrl = ensureEndsWithSlash(umbracoBaseUrl)

  const umbracoDeliveryPreviewApiKey =
    process.env.UMBRACO_DELIVERY_PREVIEW_API_KEY
  if (!umbracoDeliveryPreviewApiKey) {
    throw new Error(
      `Umbraco delivery preview API key not configured, value: ${umbracoDeliveryPreviewApiKey}`,
    )
  }

  const umbracoFormsDeliveryApiKey = process.env.UMBRACO_FORMS_DELIVERY_API_KEY
  if (!umbracoFormsDeliveryApiKey) {
    throw new Error(
      `Umbraco Forms Delivery API key not configured, value: ${umbracoFormsDeliveryApiKey}`,
    )
  }

  const recaptchaSiteKey = process.env.RECAPTCHA_SITE_KEY
  if (!recaptchaSiteKey) {
    throw new Error(
      `Recaptcha Site Key not configured, value: ${recaptchaSiteKey}`,
    )
  }

  const gtmId = process.env.GTM_ID
  if (!gtmId) {
    throw new Error(`GTM ID not configured, value: ${gtmId}`)
  }

  const cookieBotDomainGroupId = process.env.COOKIEBOT_DOMAIN_GROUP_ID
  if (!cookieBotDomainGroupId) {
    throw new Error(
      `CookieBot Domain Group ID not configured, value: ${cookieBotDomainGroupId}`,
    )
  }

  const draftToken = process.env.DRAFT_TOKEN
  if (!draftToken) {
    throw new Error(`Draft Token is not configured, value: ${draftToken}`)
  }

  const revalidateToken = process.env.REVALIDATE_TOKEN
  if (!revalidateToken) {
    throw new Error(
      `Revalidate token is not configured, value: ${revalidateToken}`,
    )
  }

  let applicationBaseUrl = process.env.APPLICATION_BASE_URL
  if (!applicationBaseUrl) {
    throw new Error(
      `Applicaiton base URL is not configured, value: ${applicationBaseUrl}`,
    )
  }
  applicationBaseUrl = ensureEndsWithSlash(applicationBaseUrl)

  const cacheOverviewDataTTLConfigEntry =
    process.env.CACHE_OVERVIEW_DATA_TTL_IN_SECONDS
  if (!cacheOverviewDataTTLConfigEntry) {
    throw new Error(
      `CACHE_OVERVIEW_DATA_TTL_IN_SECONDS shoud be configured, value was: ${cacheOverviewDataTTLConfigEntry}`,
    )
  }

  const cacheOverviewDataTTLInSeconds = Number(
    cacheOverviewDataTTLConfigEntry,
  )
  if (
    isNaN(cacheOverviewDataTTLInSeconds) ||
    typeof cacheOverviewDataTTLInSeconds !== 'number'
  ) {
    throw new Error(
      `CACHE_OVERVIEW_DATA_TTL_IN_SECONDS shoud be a number value. Value was: ${cacheOverviewDataTTLInSeconds}`,
    )
  }

  return (environmentConfig = {
    clientEnvironment: {
      umbracoBaseUrl: umbracoBaseUrl,
      recaptchaSiteKey: recaptchaSiteKey,
      cookieBotDomainGroupId: cookieBotDomainGroupId,
      cmsHomePageCulture: cmsHomePageCulture,
      cmsHomePageRoute: cmsHomePageRoute,
      cmsStartItem: cmsStartItem,
    },
    cmsHomePageRoute: cmsHomePageRoute,
    umbracoDeliveryPreviewApiKey: umbracoDeliveryPreviewApiKey,
    gtmId: gtmId,
    draftToken: draftToken,
    revalidateToken: revalidateToken,
    applicationBaseUrl: applicationBaseUrl,
    umbracoFormsDeliveryApiKey: umbracoFormsDeliveryApiKey,
    cacheOverviewDataTTLInSeconds: cacheOverviewDataTTLInSeconds,
    algoliaAppId: algoliaAppId,
    algoliaSearchApiKey: algoliaSearchApiKey,
    algoliaIndexName: algoliaIndexName,
  })
}

function ensureEndsWithSlash(string: string) {
  if (string?.substring(string.length - 1) !== '/') {
    string = `${string}/`
  }
  return string
}
